import styled from "styled-components";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import AdminService from "../../../services/admin.service";
import { useAdmin } from "../../../context/admin";
import { useGlobal } from "../../../context/global";
import { BankStatementLine, BankStatementSource, CashAccount, CashCategory, CashMovement } from "../../../types/common.types";
import FileInput from "../../common/FileInput";
import Select from "../../common/Select";

// Mapeo cuenta real (nombre en cash_accounts) -> parser determinístico correspondiente. Solo estas
// 3 cuentas retienen plata real y tienen un extracto que se pueda descargar (ver plan de Caja).
const SOURCE_BY_ACCOUNT_NAME: Record<string, BankStatementSource> = {
    'Santander': 'santander',
    'BCI': 'bci',
    'Wise': 'wise',
}

export default function BankReconciliation() {
    const { setIsLoading } = useGlobal()
    const { setCreateCashMovementPrefill, setIsCreateCashMovementModalOpen } = useAdmin()
    const [accounts, setAccounts] = useState<CashAccount[]>([])
    const [categories, setCategories] = useState<CashCategory[]>([])
    const [accountId, setAccountId] = useState('')
    const [file, setFile] = useState<File | undefined>()
    const [importResult, setImportResult] = useState<{ inserted: number, duplicates: number } | null>(null)
    const [lines, setLines] = useState<BankStatementLine[]>([])
    const [statusFilter, setStatusFilter] = useState<'unassigned' | 'matched' | 'ignored' | ''>('unassigned')
    const [expandedLineId, setExpandedLineId] = useState('')

    const bankAccounts = useMemo(() => accounts.filter(a => SOURCE_BY_ACCOUNT_NAME[a.name]), [accounts])
    const bancosCategory = useMemo(() => categories.find(c => c.name === 'Gastos Bancarios'), [categories])

    async function loadCatalogs() {
        try {
            const [accountsRes, categoriesRes] = await Promise.all([
                AdminService.listCashAccounts(),
                AdminService.listCashCategories()
            ])
            setAccounts(accountsRes.data)
            setCategories(categoriesRes.data)
        } catch (error) {
            console.log(error)
        }
    }

    async function loadLines() {
        setIsLoading(true)
        try {
            const res = await AdminService.listBankStatementLines({ status: statusFilter || undefined })
            setLines(res.data)
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => { loadCatalogs() }, [])
    useEffect(() => { loadLines() }, [statusFilter]) // eslint-disable-line react-hooks/exhaustive-deps

    async function submitImport() {
        if (!accountId || !file) return
        const account = accounts.find(a => a.id === accountId)
        const source = account ? SOURCE_BY_ACCOUNT_NAME[account.name] : undefined
        if (!source) return
        setIsLoading(true)
        try {
            const res = await AdminService.importBankStatement(accountId, source, file)
            setImportResult({ inserted: res.data.inserted, duplicates: res.data.duplicates })
            setFile(undefined)
            await loadLines()
        } catch (error: any) {
            alert(typeof error?.response?.data === 'string' ? error.response.data : 'No se pudo importar el extracto')
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    async function ignore(line_id: string) {
        setIsLoading(true)
        try {
            await AdminService.ignoreBankLine(line_id)
            await loadLines()
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    async function unassign(line_id: string) {
        setIsLoading(true)
        try {
            await AdminService.unassignBankLine(line_id)
            await loadLines()
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    function createNewFromLine(line: BankStatementLine) {
        setCreateCashMovementPrefill({
            account_id: line.account_id,
            movement_type: line.amount >= 0 ? 'ingreso' : 'egreso',
            amount: Math.abs(line.amount),
            counterpart_name: line.description || undefined,
            bank_statement_line_id: line.id,
            execution_date: line.txn_date
        })
        setIsCreateCashMovementModalOpen(true)
    }

    function registerDifference(line: BankStatementLine, difference: number) {
        if (!bancosCategory) { alert('No existe la categoría "Gastos Bancarios" en el catálogo'); return }
        setCreateCashMovementPrefill({
            account_id: line.account_id,
            movement_type: difference < 0 ? 'egreso' : 'ingreso',
            amount: Math.abs(difference),
            category_id: bancosCategory.id,
            counterpart_name: 'Comisión ' + (accounts.find(a => a.id === line.account_id)?.name || ''),
            bank_statement_line_id: line.id,
            execution_date: line.txn_date
        })
        setIsCreateCashMovementModalOpen(true)
    }

    return (
        <Main>
            <div className="upload">
                <h3>Subir extracto</h3>
                <div className="upload-row">
                    <Select
                        label="Banco"
                        defaultNull
                        value={accountId}
                        options={bankAccounts.map(a => ({ value: a.id, name: a.name }))}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => setAccountId(e.target.value)}
                    />
                    <FileInput
                        label="Archivo (Excel/CSV)"
                        value={file}
                        deleteFile={() => setFile(undefined)}
                        accept=".xlsx, .xls, .csv"
                        onChange={(newFile: File) => setFile(newFile)}
                    />
                    <button type="button" disabled={!accountId || !file} onClick={submitImport}>Importar</button>
                </div>
                {importResult && (
                    <p className="import-result">
                        {importResult.inserted} línea(s) nueva(s) importada(s){importResult.duplicates > 0 ? `, ${importResult.duplicates} ya estaban (se ignoraron)` : ''}.
                    </p>
                )}
            </div>

            <div className="filter-toggle">
                <button type="button" className={statusFilter === 'unassigned' ? 'active' : ''} onClick={() => setStatusFilter('unassigned')}>Sin conciliar</button>
                <button type="button" className={statusFilter === 'matched' ? 'active' : ''} onClick={() => setStatusFilter('matched')}>Conciliadas</button>
                <button type="button" className={statusFilter === 'ignored' ? 'active' : ''} onClick={() => setStatusFilter('ignored')}>Ignoradas</button>
                <button type="button" className={statusFilter === '' ? 'active' : ''} onClick={() => setStatusFilter('')}>Todas</button>
            </div>

            {lines.length === 0 && <p className="empty">No hay líneas para mostrar</p>}
            {lines.map(line => (
                <LineRow key={line.id}>
                    <div className="row-main">
                        <div className="info">
                            <span className="date">{line.txn_date.slice(0, 10).split('-').reverse().join('/')}</span>
                            <span className="desc">{line.description}</span>
                            <span className="account">{line.account?.name}</span>
                        </div>
                        <span className={`amount ${line.amount >= 0 ? 'ingreso' : 'egreso'}`}>
                            {line.amount >= 0 ? '+' : ''}${line.amount.toLocaleString('es-CL')}
                        </span>
                        {line.status === 'unassigned' && (
                            <div className="actions">
                                <button type="button" onClick={() => setExpandedLineId(expandedLineId === line.id ? '' : line.id)}>Vincular a movimiento(s)</button>
                                <button type="button" onClick={() => createNewFromLine(line)}>Crear movimiento nuevo</button>
                                <button type="button" className="ignore" onClick={() => ignore(line.id)}>Ignorar</button>
                            </div>
                        )}
                        {line.status === 'matched' && (
                            <div className="actions">
                                <span className="matched-info">{line.linked_movements_count} movimiento(s) — ${line.linked_movements_sum.toLocaleString('es-CL')}</span>
                                <button type="button" className="ignore" onClick={() => unassign(line.id)}>Deshacer</button>
                            </div>
                        )}
                        {line.status === 'ignored' && (
                            <div className="actions">
                                <span className="ignored-tag">Ignorada</span>
                                <button type="button" className="ignore" onClick={() => unassign(line.id)}>Deshacer</button>
                            </div>
                        )}
                    </div>
                    {expandedLineId === line.id && (
                        <LinkPicker line={line} onDone={() => { setExpandedLineId(''); loadLines() }} onRegisterDifference={registerDifference} />
                    )}
                </LineRow>
            ))}
        </Main>
    )
}

// Selector de uno o varios movimientos ya existentes en Caja para vincular a esta línea — mismo
// patrón que el checklist de deudas de Traspasos (checkbox + suma corriendo), pero buscando entre
// movimientos sin conciliar en vez de deudas.
function LinkPicker({ line, onDone, onRegisterDifference }: { line: BankStatementLine, onDone: () => void, onRegisterDifference: (line: BankStatementLine, difference: number) => void }) {
    const { setIsLoading } = useGlobal()
    const [q, setQ] = useState('')
    const [candidates, setCandidates] = useState<CashMovement[]>([])
    const [selected, setSelected] = useState<Record<string, CashMovement>>({})

    useEffect(() => {
        async function search() {
            try {
                const res = await AdminService.searchReconciliationCandidates(line.id, q || undefined)
                setCandidates(res.data)
            } catch (error) {
                console.log(error)
            }
        }
        search()
    }, [line.id, q])

    function toggle(m: CashMovement) {
        setSelected(prev => {
            const next = { ...prev }
            if (next[m.id]) delete next[m.id]
            else next[m.id] = m
            return next
        })
    }

    const selectedSum = Object.values(selected).reduce((a, m) => a + Number(m.amount), 0)
    const lineAbs = Math.abs(line.amount)
    const difference = lineAbs - selectedSum

    async function confirm() {
        if (Object.keys(selected).length === 0) return
        setIsLoading(true)
        try {
            await AdminService.assignLineToMovements(line.id, Object.keys(selected))
            onDone()
        } catch (error: any) {
            alert(typeof error?.response?.data === 'string' ? error.response.data : 'No se pudo vincular')
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    // "Registrar diferencia" también tiene que vincular lo seleccionado primero — si solo abriera
    // el modal de la comisión sin confirmar el vínculo, los movimientos marcados nunca quedarían
    // asociados a la línea (quedaría "conciliada" con la comisión sola, viéndose resuelta sin estarlo).
    async function confirmAndRegisterDifference() {
        if (Object.keys(selected).length === 0) return
        setIsLoading(true)
        try {
            await AdminService.assignLineToMovements(line.id, Object.keys(selected))
            onDone()
            onRegisterDifference(line, difference)
        } catch (error: any) {
            alert(typeof error?.response?.data === 'string' ? error.response.data : 'No se pudo vincular')
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <PickerBox>
            <input
                type="text"
                placeholder="Buscar por contraparte..."
                value={q}
                onChange={e => setQ(e.target.value)}
            />
            <ul>
                {candidates.length === 0 && <li className="empty">Sin resultados</li>}
                {candidates.map(m => (
                    <li key={m.id}>
                        <label>
                            <input type="checkbox" checked={!!selected[m.id]} onChange={() => toggle(m)} />
                            {m.counterpart_name || '(sin contraparte)'} — {m.category?.name} — ${Number(m.amount).toLocaleString('es-CL')}
                            <span className="movement-date">{new Date(m.created_at).toLocaleDateString('es-CL')}</span>
                        </label>
                    </li>
                ))}
            </ul>
            <div className="summary">
                Seleccionado: ${selectedSum.toLocaleString('es-CL')} de ${lineAbs.toLocaleString('es-CL')}
                {Math.abs(difference) > 0.01 && (
                    <span className="difference"> — diferencia: ${difference.toLocaleString('es-CL')}</span>
                )}
            </div>
            <div className="picker-actions">
                <button type="button" disabled={Object.keys(selected).length === 0} onClick={confirm}>Confirmar vínculo</button>
                {Math.abs(difference) > 0.01 && Object.keys(selected).length > 0 && (
                    <button type="button" className="secondary" onClick={confirmAndRegisterDifference}>
                        Vincular y registrar diferencia como Gastos Bancarios
                    </button>
                )}
            </div>
        </PickerBox>
    )
}

const Main = styled.div`
margin: 20px 0;
.upload {
    background: ${props => props.theme.whiteBone};
    border-radius: 10px;
    padding: 15px;
    margin-bottom: 20px;
    h3 { margin-bottom: 10px; font-size: 16px; }
    .upload-row {
        display: flex;
        align-items: flex-end;
        gap: 15px;
        flex-wrap: wrap;
        button {
            padding: 10px 16px;
            border-radius: 8px;
            border: 2px solid ${props => props.theme.blue};
            background: ${props => props.theme.blue};
            color: ${props => props.theme.white};
            cursor: pointer;
            font-weight: 600;
            &:disabled { opacity: 0.5; cursor: not-allowed; }
        }
    }
    .import-result {
        margin-top: 10px;
        font-size: 13px;
        color: ${props => props.theme.gray};
    }
}
.filter-toggle {
    display: flex;
    gap: 10px;
    margin-bottom: 15px;
    button {
        padding: 6px 14px;
        border-radius: 8px;
        border: 2px solid ${props => props.theme.blue};
        background: transparent;
        color: ${props => props.theme.blue};
        cursor: pointer;
        font-weight: 600;
    }
    button.active {
        background: ${props => props.theme.blue};
        color: ${props => props.theme.white};
    }
}
.empty { color: ${props => props.theme.gray}; text-align: center; margin: 20px 0; }
`

const LineRow = styled.div`
background: ${props => props.theme.whiteBackground};
border-radius: 10px;
box-shadow: 0 3px 5px ${props => props.theme.blackTransparent};
margin-bottom: 10px;
padding: 12px 15px;
.row-main {
    display: flex;
    align-items: center;
    gap: 15px;
    flex-wrap: wrap;
    justify-content: space-between;
}
.info {
    display: flex;
    gap: 12px;
    align-items: center;
    flex: 1;
    min-width: 260px;
    .date { font-size: 12px; color: ${props => props.theme.gray}; white-space: nowrap; }
    .desc { font-size: 14px; }
    .account { font-size: 11px; color: ${props => props.theme.gray}; background: ${props => props.theme.whiteBone}; padding: 2px 8px; border-radius: 6px; white-space: nowrap; }
}
.amount {
    font-weight: 600;
    white-space: nowrap;
}
.amount.ingreso { color: ${props => props.theme.blue}; }
.amount.egreso { color: ${props => props.theme.lightRed}; }
.actions {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
    button {
        padding: 6px 10px;
        border-radius: 6px;
        border: 2px solid ${props => props.theme.blue};
        background: transparent;
        color: ${props => props.theme.blue};
        cursor: pointer;
        font-size: 12px;
        font-weight: 600;
    }
    button.ignore {
        border-color: ${props => props.theme.lightRed};
        color: ${props => props.theme.lightRed};
    }
    .matched-info {
        font-size: 12px;
        color: ${props => props.theme.gray};
    }
}
.ignored-tag {
    font-size: 12px;
    color: ${props => props.theme.gray};
    font-style: italic;
}
`

const PickerBox = styled.div`
margin-top: 12px;
padding-top: 12px;
border-top: 1px solid ${props => props.theme.whiteBone};
input[type="text"] {
    width: 100%;
    padding: 8px 10px;
    border-radius: 6px;
    border: 1px solid ${props => props.theme.gray};
    font-size: 13px;
    box-sizing: border-box;
    margin-bottom: 8px;
}
ul {
    list-style: none;
    max-height: 220px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 8px;
    li.empty { color: ${props => props.theme.gray}; font-size: 13px; padding: 6px 0; }
    li label {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        cursor: pointer;
        padding: 4px 0;
        .movement-date { margin-left: auto; color: ${props => props.theme.gray}; font-size: 11px; }
    }
}
.summary {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 10px;
    .difference { color: ${props => props.theme.lightRed}; font-weight: 400; }
}
.picker-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    button {
        padding: 8px 14px;
        border-radius: 8px;
        border: 2px solid ${props => props.theme.blue};
        background: ${props => props.theme.blue};
        color: ${props => props.theme.white};
        cursor: pointer;
        font-weight: 600;
        font-size: 13px;
        &:disabled { opacity: 0.5; cursor: not-allowed; }
    }
    button.secondary {
        background: transparent;
        color: ${props => props.theme.blue};
    }
}
`
